import * as Sentry from '@sentry/core';
import Emittery from 'emittery';

import debugConsole from './debugConsole';
import { convertToError } from './util/convertToError';

/**
 * Event emitter into which trackError emits an event whenever it is called. This allows us to test
 * for reported errors in tests.
 */
const trackErrorEmitterInternal = new Emittery.Typed<{ error: unknown }>();
const trackErrorEmitter: Omit<
  typeof trackErrorEmitterInternal,
  'emit' | 'emitSerial' | 'clearListeners' | 'bindMethods'
> = trackErrorEmitterInternal;

export { trackErrorEmitter };

/**
 * Reports a an error from a promise chain.
 *
 * Use this if you trigger a promise from a context where you can't await it and want the error to
 * be surfaced somewhere.
 *
 * It always logs the error to debugConsole. If sentry is available (that is in production builds), the
 * error will also be reported there.
 */
export function trackError(errorObject: unknown) {
  // emit event for reported error, for use in tests
  trackErrorEmitterInternal.emit('error', errorObject).catch(suppressError);

  const error = convertToError(errorObject);
  errorToDebugConsole(error, errorObject);

  Sentry.withScope((localScope) => {
    localScope.setExtra('__serialized__', errorObject);
    Sentry.captureException(error);
  });
}

function errorToDebugConsole(error: Error, errorObject: unknown) {
  // We can use object reference comparison here, as we assign error to errorObject in the relevant case.
  if (error === errorObject) {
    debugConsole.error(error);
  } else {
    debugConsole.error(error, errorObject);
  }
}

/**
 * Suppresses the in a promise chain. The error is not reported anywhere, it is simply gone.
 *
 * CAUTION: Only use this if an error might be thrown and you actually don't care about it.
 *
 * If you expect that no error will ever be thrown use trackError instead. This allows
 * unexpected errors to still show up in out error reporting.
 */
export function suppressError(_e: any) {
  // this error is now gone forever
}

export function promiseWithResolvers<T>() {
  let resolve: (result: T | PromiseLike<T>) => void;
  let reject: (reason?: unknown) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve: resolve!, reject: reject! };
}
